import { memo, useCallback } from 'react';
import { Box, Flex, Text } from '@chakra-ui/react';
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { useDispatch, useSelector } from 'react-redux';

import { getBlockDriversData, getSelectedDriver, setDriver } from 'redux/PlanPageSlice';
import { resetDimension } from 'redux/DimensionPageSlice';
import { resetIndicator } from 'redux/IndicatorPageSlice';
import { resetItem, resetTableOrgChart } from 'redux/ItemPageSlice';
import { AppDispatch } from 'utils/GlobalHelpers';

import { PLAN_INPUT_PANEL_WIDTH } from '../planInputPanelTheme';
import {
  PLAN_HEADER_HORIZONTAL_PADDING,
  PLAN_REVAMP_RADIUS_SM,
  PLAN_TEXT,
  accent,
  neutral,
  text as textToken,
} from '../planPageRevampUi';

const getDriverName = (driver: any): string => {
  if (driver?.type === 'indicator') return driver?.name ?? '';
  if (driver?.type === 'dimension') return driver?.dim_name ?? '';
  return driver?.property_name ?? '';
};

const getDriverKey = (driver: any, index: number): string => {
  if (driver?.type === 'indicator') return `indicator-${driver?.id ?? index}`;
  if (driver?.type === 'dimension') return `dimension-${driver?.dim_id ?? index}`;
  return `property-${driver?.property_id ?? index}`;
};

const isSameDriver = (driver: any, selectedDriver: any): boolean => {
  if (!driver || !selectedDriver || driver.type !== selectedDriver.type) return false;
  if (driver.type === 'indicator') return driver.id === selectedDriver.id;
  if (driver.type === 'dimension') return driver.dim_id === selectedDriver.dim_id;
  return driver.property_id === selectedDriver.property_id;
};

interface DriverCardProperties {
  driver: any;
  isSelected: boolean;
  onSelect: (driver: any) => void;
}

/**
 * One driver row. Idle state is greyscale (`neutral.border`, white surface);
 * accent — `accent.solid` border, `accent.subtle` fill — is reserved for the
 * selected driver, per the rule the rest of the revamp follows. This used a
 * permanent lavender fill (`PLAN_KPI_SELECTED_BG`) on every row regardless of
 * selection, which made every driver look active at once.
 */
const DriverInputCard = memo(({ driver, isSelected, onSelect }: DriverCardProperties) => {
  const handleClick = useCallback(() => onSelect(driver), [driver, onSelect]);
  const driverName = getDriverName(driver);

  return (
    <Flex
      as='button'
      type='button'
      onClick={handleClick}
      alignItems='center'
      justifyContent='space-between'
      w='100%'
      p={3}
      gap={2}
      border='1px solid'
      borderColor={isSelected ? accent.solid : neutral.border}
      borderRadius={PLAN_REVAMP_RADIUS_SM}
      bg={isSelected ? accent.solid : neutral.surface}
      cursor='pointer'
      textAlign='left'
      transition='border-color 0.15s ease, background 0.15s ease'
      _hover={{ borderColor: accent.solid }}
    >
      <Flex minW={0} alignItems='center' gap={1} {...PLAN_TEXT.labelSmMedium} lineHeight='normal'>
        <Text color={isSelected ? neutral.surface : textToken.primary} whiteSpace='nowrap'>
          Enter your
        </Text>
        <Text color={isSelected ? neutral.surface : accent.solid} fontWeight={600} noOfLines={1}>
          {driverName}
        </Text>
      </Flex>
      <ChevronRightIcon
        boxSize={4}
        color={isSelected ? neutral.surface : textToken.secondary}
        flexShrink={0}
        transform={isSelected ? 'rotate(180deg)' : undefined}
      />
    </Flex>
  );
});

DriverInputCard.displayName = 'DriverInputCard';

interface Properties {
  onCollapse: () => void;
}

const InputsPanel = ({ onCollapse }: Properties) => {
  const dispatch: AppDispatch = useDispatch();
  const blockDrivers = useSelector(getBlockDriversData);
  const selectedDriver = useSelector(getSelectedDriver);

  const onSelectDriver = useCallback(
    (driver: any) => {
      if (isSameDriver(driver, selectedDriver)) {
        dispatch(setDriver(null));
        return;
      }

      if (selectedDriver?.type === 'indicator') {
        dispatch(setDriver(null));
        dispatch(resetIndicator());
      }
      if (selectedDriver?.type === 'property') {
        dispatch(setDriver(null));
        dispatch(resetDimension());
      }
      if (selectedDriver?.type === 'dimension') {
        dispatch(setDriver(null));
        dispatch(resetTableOrgChart());
        dispatch(resetItem());
      }

      dispatch(setDriver(driver));
    },
    [dispatch, selectedDriver],
  );

  return (
    <Flex
      direction='column'
      w={PLAN_INPUT_PANEL_WIDTH}
      minW={PLAN_INPUT_PANEL_WIDTH}
      flexShrink={0}
      h='100%'
      bg={neutral.surface}
      borderRight='1px solid'
      borderColor={neutral.border}
      overflow='hidden'
    >
      <Box px={PLAN_HEADER_HORIZONTAL_PADDING} pt='12px' flexShrink={0}>
        <Flex alignItems='center' justifyContent='space-between' gap={2}>
          <Text {...PLAN_TEXT.inputsPanelTitle} color={textToken.primary}>
            Inputs
          </Text>
          <Flex
            as='button'
            type='button'
            aria-label='Collapse inputs panel'
            alignItems='center'
            justifyContent='center'
            h='28px'
            w='28px'
            minW='28px'
            flexShrink={0}
            border='1px solid'
            borderColor={neutral.border}
            borderRadius={PLAN_REVAMP_RADIUS_SM}
            bg={neutral.surface}
            color={textToken.secondary}
            cursor='pointer'
            transition='background 0.12s ease, border-color 0.12s ease, color 0.12s ease'
            _hover={{
              bg: neutral.surfaceHover,
              borderColor: neutral.borderStrong,
              color: accent.solid,
            }}
            onClick={onCollapse}
          >
            <ChevronLeftIcon boxSize={4} />
          </Flex>
        </Flex>
        <Text mt={1.5} {...PLAN_TEXT.labelSm} color={textToken.secondary}>
          Enter and adjust your inputs here
        </Text>
      </Box>

      <Box
        flex='1'
        px={PLAN_HEADER_HORIZONTAL_PADDING}
        pt='20px'
        pb='12px'
        overflowY='auto'
        display='flex'
        flexDirection='column'
        gap={3}
      >
        {(blockDrivers || []).map((driver: any, index: number) => (
          <DriverInputCard
            key={getDriverKey(driver, index)}
            driver={driver}
            isSelected={isSameDriver(driver, selectedDriver)}
            onSelect={onSelectDriver}
          />
        ))}
      </Box>
    </Flex>
  );
};

export default memo(InputsPanel);
